import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {environment} from '@src/environments/environment';
import {Field, PaginatedResult, PublicAccount, PublicVehicle, SingleResult, Vehicle, VehicleData, WorkflowExcerpt} from '@app/interfaces';
import {AnchorPriceDeclarationState, StorePriceHistoryRequest, VehiclePriceHistory, VehiclePriceType, VehiclePriceChangeType} from '@app/interfaces/entities/vehicle-price-history';
import {CreateVehicleActivityRequest, UpdateVehicleActivityRequest, VehicleActivity} from '@app/interfaces/entities/vehicle-activity';
import {AdsSummaryResult} from '@app/interfaces/advertising/ads-summary';
import {switchMap, tap} from 'rxjs/operators';
import {DOCUMENT} from '@angular/common';
import {Form} from '@app/interfaces/entities/form';
import {ArrayResult} from '@app/interfaces/responses/array-result';
import {ImportedVehicle} from '@app/interfaces/integration/imported-vehicle';
import {LocaleEnum} from '@app/enums/LocaleEnum';
import {PaginationFilter} from '@app/interfaces/pagination/paginationFilter';
import {ApiHelperService} from '@app/util/api-helper.service';
import {AdvertisingForm} from '@app/interfaces/entities/advertising-form';
import {RvcPreviewInputs, RvcPreviewResponse} from '@app/dialogs/vehicle-rvc-preview-dialog/vehicle-rvc-preview-dialog.component';
import {VehicleMissingFields} from '@app/interfaces/entities/vehicle-missing-fields';

@Injectable({
  providedIn: 'root'
})
export class VehicleApiService {

  private vehicleRefreshStream: Subject<Vehicle> = new BehaviorSubject(null);

  constructor(private http: HttpClient,
              @Inject(DOCUMENT) private document: Document,
              @Inject(LOCALE_ID) private locale: string,
  ) {
  }

  getVehicleRefreshStream() {
    return this.vehicleRefreshStream;
  }

  getPaginatedVehicles(paginationFilter: PaginationFilter):
    Observable<PaginatedResult<Vehicle[]>> {
    const params = ApiHelperService.buildPaginationParams(paginationFilter);

    return this.http.get<PaginatedResult<Vehicle[]>>(`${environment.apiUrl}/api/vehicles`, {params});
  }

  getPaginatedImportedVehicles(paginationFilter: PaginationFilter):
    Observable<PaginatedResult<ImportedVehicle[]>> {
    const params = ApiHelperService.buildPaginationParams(paginationFilter);

    return this.http.get<PaginatedResult<ImportedVehicle[]>>(`${environment.apiUrl}/api/integrations/vehicle-import/list`, {params});
  }

  getPaginatedSoldVehicles(paginationFilter: PaginationFilter):
    Observable<PaginatedResult<Vehicle[]>> {
    const params = ApiHelperService.buildPaginationParams(paginationFilter);

    return this.http.get<PaginatedResult<Vehicle[]>>(`${environment.apiUrl}/api/sold-vehicles`, {params});
  }

  getPaginatedVehiclesAds(paginationFilter: PaginationFilter):
    Observable<PaginatedResult<Vehicle[]>> {
    const params = ApiHelperService.buildPaginationParams(paginationFilter);

    return this.http.get<PaginatedResult<Vehicle[]>>(`${environment.apiUrl}/api/vehicles/ads`, {params});
  }

  /**
   * Counters for the advertising list's publish-state control. `portals` scopes
   * them the same way the list is scoped — with a portal filter on, "published"
   * means live on one of those portals, not live anywhere (BRIEFD-6853).
   */
  getAdsSummary(portals: string[]): Observable<SingleResult<AdsSummaryResult>> {
    let params = new HttpParams();
    portals.forEach(type => params = params.append('adIntegration[]', type));

    return this.http.get<SingleResult<AdsSummaryResult>>(`${environment.apiUrl}/api/vehicles/ads/summary`, {params});
  }

  getVehicle(vehicleUuid: string): Observable<SingleResult<Vehicle>> {
    return this.http.get<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}`);
  }

  getVehicleData(vehicleUuid: string): Observable<SingleResult<VehicleData>> {
    return this.http.get<SingleResult<VehicleData>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/data`);
  }

  checkLeasingValidation(vehicleUuid: string): Observable<SingleResult<{ isValid: boolean; missingFields: string[]; vehicleUuid: string }>> {
    return this.http.get<SingleResult<{ isValid: boolean; missingFields: string[]; vehicleUuid: string }>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/leasing-validation`);
  }

  /**
   * Fetch the vehicle add/edit form.
   *
   * @param vehicleUuid  The vehicle UUID (omit when adding a new vehicle).
   * @param v2           When true, request the Design 2.0 form layout (5 tabs:
   *                     Osnovne / Fotografije / Oprema / Nabava / Prodaja).
   *                     Legacy callers must omit this to keep the existing
   *                     3-tab structure.
   */
  getForm(vehicleUuid?: string, v2: boolean = false, isValuation: boolean = false): Observable<SingleResult<Form>> {

    let params = new HttpParams();
    params = params.append('type', 'vehicle');
    params = params.append('vehicleUuid', vehicleUuid);
    if (v2) {
      params = params.append('v', '2');
    }
    // Keeps the next stock internal number off a new valuation (BRIEFD-7464).
    if (isValuation) {
      params = params.append('isValuation', '1');
    }

    return this.http.get<SingleResult<Form>>(`${environment.apiUrl}/api/forms`, {params});
  }

  /**
   * The form as it would come back after saving `values`, without saving them.
   * The advertising tab is derived from the stored vehicle (business unit →
   * portals, brand/model → per-portal manufacturer/model, ad title), so
   * unsaved changes only show up there through this call (BRIEFD-7189).
   */
  previewForm(vehicleUuid: string | null, values: { [key: string]: any }): Observable<SingleResult<Form>> {
    let params = new HttpParams();
    params = params.append('type', 'vehicle');
    params = params.append('v', '2');
    if (vehicleUuid) {
      params = params.append('vehicleUuid', vehicleUuid);
    }

    return this.http.post<SingleResult<Form>>(`${environment.apiUrl}/api/forms/preview`, {...values}, {params});
  }

  getQuickForm(vehicleUuid?: string): Observable<SingleResult<Form>> {
    let params = new HttpParams();
    params = params.append('type', 'vehicle');
    params = params.append('formType', 'quick');
    params = params.append('vehicleUuid', vehicleUuid);

    return this.http.get<SingleResult<Form>>(`${environment.apiUrl}/api/forms`, {params});
  }

  getPublishForm(vehicleUuid: string): Observable<SingleResult<AdvertisingForm>> {
    return this.http.get<SingleResult<AdvertisingForm>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/publish-form`);
  }

  /**
   * `v2=true` tells the BE to rebuild the v2 (5-tab superset) form for
   * validation + `prepareValuesForDatabase`. Without it, fields that only
   * exist in v2 (current_damages, state booleans, the v2 Nabava/Prodaja
   * groupings, etc.) are stripped on save. Default is `false` so legacy
   * callers (fast/quick form, quick-create dialog, legacy edit page) keep
   * the 3-tab behaviour; the v2 form page passes `true` explicitly.
   *
   * Note: BE rejects `formType=quick + v=2` with 422 — there is no v2 quick
   * form, so do not pass `v2=true` for the quick path.
   */
  store(fields: { [key: string]: any }, formType: string, type: string = null, v2: boolean = false): Observable<SingleResult<Vehicle>> {
    let params = new HttpParams();
    if (type) {
      params = params.append('type', type);
    }
    if (v2) {
      params = params.append('v', '2');
    }

    switch (formType) {
      case 'quick': {
        params = params.append('formType', 'quick');
        return this.http.post<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles`, {...fields}, {params});
      }
      default: {
        return this.http.post<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles`, {...fields}, {params});
      }
    }
  }

  update(entityUuid: string, fields: { [key: string]: any }, v2: boolean = false): Observable<SingleResult<Vehicle>> {
    let params = new HttpParams();
    if (v2) {
      params = params.append('v', '2');
    }
    return this.http.put<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${entityUuid}`, {...fields}, {params});
  }

  archive(entityUuid: string) {
    return this.http.post<SingleResult<any[]>>(`${environment.apiUrl}/api/vehicles/${entityUuid}/archive`, {});
  }

  bulkArchive(vehicleUuids: string[]) {
    return this.http.post<SingleResult<any[]>>(`${environment.apiUrl}/api/vehicles/bulk-archive`, { vehicleUuids });
  }

  setAsSold(entityUuid: string) {
    return this.http.post<SingleResult<any[]>>(`${environment.apiUrl}/api/vehicles/${entityUuid}/set-as-sold`, {});
  }

  setAsInStock(entityUuid: string) {
    return this.http.post<SingleResult<any[]>>(`${environment.apiUrl}/api/vehicles/${entityUuid}/set-as-in-stock`, {});
  }

  setAsReserved(entityUuid: string) {
    return this.http.post<SingleResult<any[]>>(`${environment.apiUrl}/api/vehicles/${entityUuid}/set-as-reserved`, {});
  }

  unarchive(entityUuid: string) {
    return this.http.post<SingleResult<any[]>>(`${environment.apiUrl}/api/vehicles/${entityUuid}/unarchive`, {});
  }

  moveValuationToStock(entityUuid: string) {
    return this.http.post<SingleResult<any[]>>(`${environment.apiUrl}/api/vehicles/${entityUuid}/valuation-to-stock`, {});
  }

  delete(vehicleUuid: string) {
    return this.http.delete(`${environment.apiUrl}/api/vehicles/${vehicleUuid}`);
  }

  getFieldsForVehicles(entityUuid: string): Observable<SingleResult<Form>> {
    return this.http.get<SingleResult<Form>>(`${environment.apiUrl}/api/vehicles/${entityUuid}/form`);
  }

  updateStatus(vehicleUuid: string, statusMachineUuid: string, statusUuid: string): Observable<SingleResult<Vehicle>> {
    return this.http.put<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/status`, {
      statusMachineUuid,
      statusUuid
    });
  }

  updateEntityGroups(vehicleUuid: string, entityGroups: string[]): Observable<SingleResult<Vehicle>> {
    return this.http.put<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/entity-groups`, {
      entityGroups
    });
  }

  publish(vehicleUuid: string, fields: { [key: string]: any }): Observable<SingleResult<Vehicle>> {
    return this.http.post<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/publish`, {...fields});
  }

  updatePublish(vehicleUuid: string, fields: { [key: string]: any }): Observable<SingleResult<Vehicle>> {
    return this.http.put<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/publish`, {...fields});
  }

  unpublish(vehicleUuid: string): Observable<SingleResult<Vehicle>> {
    return this.http.post<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/unpublish`, {});
  }

  getMarketAnalysis(vehicleUuid: string) {
    return this.http.get(`${environment.apiUrl}/api/market-study/summary`, {params: {vehicleUuid}});
  }

  patchField(vehicleUuid: string, fieldUuid: string, value: any, priceChangeReason?: string, priceChangeDescription?: string): Observable<SingleResult<Vehicle>> {
    const payload: any = {
      fields: [{
        fieldUuid,
        value
      }]
    };

    if (priceChangeReason) {
      payload.priceChangeReason = priceChangeReason;
    }
    if (priceChangeDescription) {
      payload.priceChangeDescription = priceChangeDescription;
    }

    return this.http.patch<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/fields`, payload).pipe(tap((result) => {
      this.vehicleRefreshStream.next(result.data);
    }));
  }

  patchFields(vehicleUuid: string, fields, priceChangeReason?: string, priceChangeDescription?: string, photographsBaseline?: string[]): Observable<SingleResult<Vehicle>> {
    const payload: any = {
      fields
    };

    if (photographsBaseline) {
      payload.photographsBaseline = photographsBaseline;
    }

    if (priceChangeReason) {
      payload.priceChangeReason = priceChangeReason;
    }
    if (priceChangeDescription) {
      payload.priceChangeDescription = priceChangeDescription;
    }

    return this.http.patch<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/fields`, payload).pipe(tap((result) => {
      this.vehicleRefreshStream.next(result.data);
    }));
  }

  updateEntityCategory(vehicleUuid: string, entityCategoryUuid: string) {
    return this.http.put<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/entity-category`, {
      entityCategoryUuid
    });
  }

  getField(vehicleUuid: string, fieldUuid: string): Observable<SingleResult<Field<any>>> {
    return this.http.get<SingleResult<Field<any>>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/fields/${fieldUuid}`);
  }

  /** Clients captured via the Virtual Salon forms for this vehicle. */
  getVirtualSalonContacts(vehicleUuid: string): Observable<ArrayResult<{
    uuid: string;
    name: string;
    organizationName?: string;
    email?: string;
    phone?: string;
    source?: string;
    createdAt?: string;
  }>> {
    return this.http.get<ArrayResult<any>>(
      `${environment.apiUrl}/api/vehicles/${vehicleUuid}/virtual-salon-contacts`,
    );
  }

  getFields(vehicleUuid: string, typeIds: string[]): Observable<ArrayResult<Field<any>>> {
    return this.http.post<ArrayResult<Field<any>>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/fields`, {fields: typeIds});
  }

  getPhotoStatus(vehicleUuid: string): Observable<{pendingCount: number; uploadingOriginals: number; uploadingProcessed: number; changedAt: string | null}> {
    return this.http.get<{pendingCount: number; uploadingOriginals: number; uploadingProcessed: number; changedAt: string | null}>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/photo-status`);
  }

  getDiscountFields(vehicleUuid: string): Observable<ArrayResult<Field<any>>> {
    return this.http.get<ArrayResult<Field<any>>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/discount-fields`);
  }

  patchDiscountFields(
    vehicleUuid: string,
    discountType: any,
    discountedPrice: any,
    advertisedPrice?: any,
    priceChangeReason?: string,
    priceChangeDescription?: string,
  ): Observable<SingleResult<any>> {
    const payload: any = {discountType, discountedPrice};
    if (advertisedPrice !== undefined) {
      payload.advertisedPrice = advertisedPrice;
    }
    if (priceChangeReason) {
      payload.priceChangeReason = priceChangeReason;
    }
    if (priceChangeDescription) {
      payload.priceChangeDescription = priceChangeDescription;
    }
    return this.http.patch<SingleResult<any>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/discount-fields`, payload);
  }

  getPublicUrlForVehicle(vehicleUuid: string, locale: string) {
    return `${environment.apiUrl}/${locale}/public/vehicles/${vehicleUuid}`;
  }

  getPublicUrlForVehicleV2(vehicleUuid: string, locale: string) {
    return `${environment.apiUrl}/${locale}/public/v2/virtual-salon/vehicles/${vehicleUuid}`;
  }

  /** `url` is the Virtual Salon page only; `bestUrl` is the share-anywhere link —
   *  website first, then the country's primary portal (BRIEFD-7429). */
  getPublicLinkForVehicle(vehicleUuid: string): Observable<{data: {url: string | null, bestUrl: string | null, websiteNotLive?: boolean}}> {
    return this.http.get<{data: {url: string | null, bestUrl: string | null, websiteNotLive?: boolean}}>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/public-link`);
  }

  extractPublicVehicleName(vehicle: PublicVehicle) {
    if (vehicle && vehicle.vehicle) {
      return `${vehicle.vehicle.brand} ${vehicle.vehicle.name}`;
    }

    return '-';
  }

  extractPublicVehicleVersion(vehicle: PublicVehicle) {
    if (vehicle?.vehicle?.version) {
      return `${vehicle.vehicle.version} ${vehicle.vehicle.engine}`;
    }

    return '-';
  }

  extractVehicleDescription(vehicle: Vehicle | ImportedVehicle) {
    const descriptions = [];

    if (vehicle.vehicle) {
      descriptions.push(vehicle.vehicle.brand);
    }

    if (vehicle.modelYear) {
      descriptions.push(vehicle.modelYear);
    }

    if (vehicle.customVehicleType) {
      descriptions.push(vehicle.customVehicleType);
    }

    if (vehicle.vehicle?.version) {
      descriptions.push(vehicle.vehicle.version);
    }

    return descriptions.join(', ');
  }

  getVehicleWorkflows(entityUuid: string): Observable<ArrayResult<WorkflowExcerpt>> {
    return this.http.get<ArrayResult<WorkflowExcerpt>>(`${environment.apiUrl}/api/vehicles/${entityUuid}/workflows`);
  }

  getMappedVehicles(vehicleUuids: any[]) {
    let params = new HttpParams();

    for (const vehicleUuid of vehicleUuids) {
      params = params.append('vehicleUuids[]', vehicleUuid);
    }

    return this.http.get<SingleResult<any>>(`${environment.apiUrl}/api/helpers/vehicles/map`, {params});
  }

  getVehicleInvoices(vehicleUuid: string, sort: { column: string, order: string }, filter: {}, allowFalsy = false): Observable<PaginatedResult<any>> {
    let params = new HttpParams();

    if (sort.column) {
      params = params.append('sortBy', sort.column.toString());

      if (sort.order) {
        params = params.append('sortOrder', sort.order.toString());
      }
    }

    const filters = [];

    for (const prop in filter) {
      if (filter.hasOwnProperty(prop)) {
        if (allowFalsy || filter[prop]) {
          filters.push({key: prop, value: filter[prop]});
        }
      }
    }

    if (filters.length) {
      filters.forEach((item) => {
        params = params.append('filter[]', `${item.key}:${item.value}`);
      });
    }

    return this.http.get<PaginatedResult<any>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/invoices`, {params});
  }

  checkVehicleActiveAdvances(vehicleUuid: string): Observable<{
    hasActiveAdvances: boolean;
    advanceCount: number;
    latestAdvance?: {
      uuid: string;
      number: string;
      reference: string;
      price_gross_sum: number;
      created_at: string;
      document_id: string
    }
  }> {
    return this.http.get<{
      hasActiveAdvances: boolean;
      advanceCount: number;
      latestAdvance?: {
        uuid: string;
        number: string;
        reference: string;
        price_gross_sum: number;
        created_at: string;
        document_id: string
      }
    }>(
      `${environment.apiUrl}/api/vehicles/${vehicleUuid}/check-active-advances`
    );
  }

  getRVCPrint(vehicleUuid: string, includePriceHistory: boolean = false) {
    return this.http.post(
      `${environment.apiUrl}/api/vehicles/${vehicleUuid}/pdf/rvc`,
      { includePriceHistory },
      {responseType: 'blob' as 'json'}
    );
  }

  getValuationPDF(vehicleUuid: string) {
    return this.http.get(
      `${environment.apiUrl}/api/vehicles/${vehicleUuid}/pdf/valuation`, {responseType: 'blob' as 'json'}
    );
  }

  /** @deprecated - use projectors */
  handleInvoiceActions(actions: { [key: string]: { [key: string]: unknown } }, currencyId: string) {
    return this.http.post(
      `${environment.apiUrl}/api/vehicles/invoice-actions`,
      {actions, currencyId},
    );
  }

  getPreviousVehicles(vehicleUuid: string) {
    return this.http.post(
      `${environment.apiUrl}/api/vehicles/${vehicleUuid}/previousVehicles`,
      {},
    );
  }

  getPriceHistory(vehicleUuid: string): Observable<VehiclePriceHistory[]> {
    return this.http.get<VehiclePriceHistory[]>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/price-history`);
  }

  storePriceHistory(vehicleUuid: string, data: StorePriceHistoryRequest): Observable<SingleResult<VehiclePriceHistory>> {
    return this.http.post<SingleResult<VehiclePriceHistory>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/price-history`, data);
  }

  getAnchorPriceDeclaration(vehicleUuid: string): Observable<SingleResult<AnchorPriceDeclarationState>> {
    return this.http.get<SingleResult<AnchorPriceDeclarationState>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/anchor-price-declaration`);
  }

  storeAnchorPriceDeclaration(vehicleUuid: string, advertisedFrom: string, price: number): Observable<SingleResult<AnchorPriceDeclarationState>> {
    return this.http.put<SingleResult<AnchorPriceDeclarationState>>(
      `${environment.apiUrl}/api/vehicles/${vehicleUuid}/anchor-price-declaration`,
      {advertised_from: advertisedFrom, price},
    );
  }

  setPriceListVisibility(vehicleUuid: string, hidden: boolean): Observable<SingleResult<AnchorPriceDeclarationState>> {
    return this.http.put<SingleResult<AnchorPriceDeclarationState>>(
      `${environment.apiUrl}/api/vehicles/${vehicleUuid}/price-list-visibility`, {hidden},
    );
  }

  getActivities(vehicleUuid: string): Observable<VehicleActivity[]> {
    return this.http.get<VehicleActivity[]>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/activities`);
  }

  createActivity(vehicleUuid: string, data: CreateVehicleActivityRequest): Observable<SingleResult<VehicleActivity>> {
    return this.http.post<SingleResult<VehicleActivity>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/activities`, data);
  }

  deleteActivity(vehicleUuid: string, activityUuid: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/activities/${activityUuid}`);
  }

  getActivity(vehicleUuid: string, activityUuid: string): Observable<SingleResult<VehicleActivity>> {
    return this.http.get<SingleResult<VehicleActivity>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/activities/${activityUuid}`);
  }

  updateActivity(vehicleUuid: string, activityUuid: string, data: UpdateVehicleActivityRequest): Observable<SingleResult<VehicleActivity>> {
    return this.http.put<SingleResult<VehicleActivity>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/activities/${activityUuid}`, data);
  }

  startActivity(vehicleUuid: string, activityUuid: string): Observable<SingleResult<VehicleActivity>> {
    return this.http.post<SingleResult<VehicleActivity>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/activities/${activityUuid}/start`, {});
  }

  completeActivity(vehicleUuid: string, activityUuid: string): Observable<SingleResult<VehicleActivity>> {
    return this.http.post<SingleResult<VehicleActivity>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/activities/${activityUuid}/complete`, {});
  }

  cancelActivity(vehicleUuid: string, activityUuid: string): Observable<SingleResult<VehicleActivity>> {
    return this.http.post<SingleResult<VehicleActivity>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/activities/${activityUuid}/cancel`, {});
  }

  getCurrentLocation(vehicleUuid: string): Observable<SingleResult<VehicleActivity | null>> {
    return this.http.get<SingleResult<VehicleActivity | null>>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/activities/current-location`);
  }

  /**
   * Updates a vehicle price field and automatically tracks the change in price history
   * @param vehicleUuid - The UUID of the vehicle
   * @param fieldUuid - The field UUID (e.g., 'briefd.vehicle.advertised_sell_price_in_eur')
   * @param newPrice - The new price value
   * @param priceType - The type of price (advertised or discounted)
   * @param description - Optional description of the price change
   * @returns Observable that completes when both price update and history logging are done
   */
  updatePriceWithHistory(
    vehicleUuid: string,
    fieldUuid: string,
    newPrice: number,
    priceType: VehiclePriceType = VehiclePriceType.ADVERTISED,
    description?: string
  ): Observable<SingleResult<Vehicle>> {
    // First, get the current price
    return this.getField(vehicleUuid, fieldUuid).pipe(
      switchMap((fieldResult) => {
        const oldPrice = fieldResult.data?.value ?? null;

        // Determine the change type based on price comparison
        let changeType: VehiclePriceChangeType;
        if (oldPrice === null || oldPrice === undefined) {
          changeType = VehiclePriceChangeType.INITIAL_PRICE;
        } else if (newPrice > oldPrice) {
          changeType = VehiclePriceChangeType.PRICE_INCREASE;
        } else if (newPrice < oldPrice) {
          changeType = VehiclePriceChangeType.PRICE_DECREASE;
        } else {
          // Price hasn't changed, just update the field without logging history
          return this.patchField(vehicleUuid, fieldUuid, newPrice);
        }

        // Update the price field
        return this.patchField(vehicleUuid, fieldUuid, newPrice).pipe(
          tap(() => {
            // Automatically log the price change to history (fire and forget)
            this.storePriceHistory(vehicleUuid, {
              price_type: priceType,
              old_price: oldPrice,
              new_price: newPrice,
              change_type: changeType,
              description: description ?? null
            }).subscribe({
              error: (err) => {
                console.error('Failed to log price history:', err);
              }
            });
          })
        );
      })
    );
  }

  getVehicleFromVin(vehicleVin: string): Observable<SingleResult<Vehicle>> {
    return this.http.get<SingleResult<Vehicle>>(`${environment.apiUrl}/api/vehicles/vin/${vehicleVin}`);
  }

  getVehicleByReferenceNumber(referenceNumber: string, excludeUuid?: string): Observable<{ exists: boolean }> {
    let url = `${environment.apiUrl}/api/vehicles/reference-number/${referenceNumber}`;
    if (excludeUuid) {
      url += `?excludeUuid=${excludeUuid}`;
    }
    return this.http.get<{ exists: boolean }>(url);
  }

  removeAds(vehicleUuid: string) {
    return this.http.post(`${environment.apiUrl}/api/vehicles/remove-ads`, {vehicleUuid});
  }

  public exportVehicleStock(filter: { q: string; entityGroupUuids: any[]; showAll: boolean; statusUuid: string[]; commissionSale: string; businessUnitUuid: string; entityCategoryUuid: string; leaseBack: string, date: string }) {
    return this.http.post(`${environment.apiUrl}/api/vehicles/export/stock`, filter, {responseType: 'blob' as 'json'});
  }

   /**
   * Which Croatia-only fields the given vehicles are still missing, with the
   * fields themselves ready to render (BRIEFD-7186).
   *
   * `context: 'document'` takes the vehicles on an estimate/invoice about to
   * be saved; `context: 'rppo'` takes the date range the export would cover
   * and resolves the vehicles server-side. An empty `data` array means
   * nothing is missing — safe to call unconditionally.
   *
   * Despite the endpoint's name it is no longer Croatia-only: the VIN is
   * required on every document in every country since BRIEFD-7429, because
   * it is no longer required on the vehicle-entry forms. A non-Croatian
   * account therefore gets back the VIN, or nothing.
   */
  public getMissingCroatianFields(params: {
    context: 'document' | 'rppo';
    vehicleUuids?: string[];
    dateFrom?: string | null;
    dateTo?: string | null;
    junkyardClauseSelected?: boolean;
    /** Croatian tax-clause fields only apply to for-sale documents; the VIN
     *  applies to all of them. Omitted means for-sale. */
    forSale?: boolean;
  }): Observable<ArrayResult<VehicleMissingFields>> {
    return this.http.post<ArrayResult<VehicleMissingFields>>(
      `${environment.apiUrl}/api/vehicles/missing-croatian-fields`, params);
  }

  public exportRppo(params: { dateFrom?: string; dateTo?: string; vehicleUuids?: string[] }) {
    return this.http.post(`${environment.apiUrl}/api/vehicles/export/rppo`, params, {responseType: 'blob' as 'json'});
  }

  public updateRppoStatus(vehicleUuid: string, rppoReported: boolean) {
    return this.http.patch<{ success: boolean }>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/rppo-status`, {rppo_reported: rppoReported});
  }

  public linkVehicleToInvoice(vehicleUuid: string, documentId: string) {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/api/vehicles/${vehicleUuid}/link-to-invoice`, {documentId});
  }

  /**
   * Get RVC preview calculation with custom parameters
   * @param vehicleUuid Vehicle UUID
   * @param params RVC calculation parameters to override
   * @returns Observable with RVC calculation result
   */
  public getCalculationRvcInputs(vehicleUuid: string): Observable<RvcPreviewInputs> {
    return this.http.get<RvcPreviewInputs>(
      `${environment.apiUrl}/api/vehicles/${vehicleUuid}/rvc/preview/calculation-inputs`
    );
  }

  public getRvcPreview(vehicleUuid: string, params: any): Observable<RvcPreviewResponse> {
    return this.http.post<RvcPreviewResponse>(
      `${environment.apiUrl}/api/vehicles/${vehicleUuid}/rvc/preview`,
      params
    );
  }
}

